import { Icon, MenuItem, MenuSeparator, Text } from "@salestrak/ui";

import {
  editableShape,
  fieldItemCount,
  formNameBlocker,
  isOfferedAnywhere,
  type Form,
  type FormsStore,
} from "#app/lib/forms";
import { showUndoToast } from "#app/lib/undo-shortcut";

const NO_QUESTIONS = "This form has no questions yet";
const NOT_PUBLISHED = "Publish this form before filling it out";
const NOT_ACCEPTING = "This form is not accepting submissions";
const NOT_OFFERED = "This form is not offered on any Project";

export function discardDraftWithUndo(store: FormsStore, form: Form): boolean {
  const discarded = form.draft;
  if (!discarded) return false;
  const landed = store.discardDraft(form.formId);
  showUndoToast({
    title: "Unpublished changes discarded",
    onUndo: () =>
      void landed.then((outcome) => {
        if (outcome.ok) store.restoreDraft(form.formId, discarded);
      }),
  });
  return true;
}

export function unpublishWithUndo(store: FormsStore, formId: string): void {
  // Undo waits for the unpublish to land; a reopen sent first finds the form still Live and does nothing.
  const landed = store.unpublishForm(formId);
  showUndoToast({
    title: "Form unpublished",
    description: "It keeps every submission and stops taking new ones.",
    onUndo: () =>
      void landed.then((outcome) => {
        if (outcome.ok) void store.reopenForm(formId);
      }),
  });
}

/** Why the fill door is shut, or null. The reasons have different fixes, so the row names which. */
export function fillBlockedReason(form: Form): string | null {
  if (fieldItemCount(form) === 0) return NO_QUESTIONS;
  if (form.status === "draft") return NOT_PUBLISHED;
  if (form.status === "closed") return NOT_ACCEPTING;
  if (!isOfferedAnywhere(form)) return NOT_OFFERED;
  return null;
}

export function publishBlockedReason(form: Form, forms: readonly Form[]): string | null {
  if (fieldItemCount(editableShape(form)) === 0) return NO_QUESTIONS;
  const published = forms.filter((other) => other.formId !== form.formId && other.status !== "draft");
  return formNameBlocker(form.name, published.map((other) => other.name))?.message ?? null;
}

/** Filling is ungated here: the Project the filler picks decides it by its own edit access. */
export function renderFormMenuItems({
  fillBlocked,
  onFill,
  onOpen,
  onRename,
  onShare,
  onDuplicate,
  publish,
  onDiscardDraft,
  onUnpublish,
  onDelete,
}: {
  fillBlocked?: string | null;
  onFill?: () => void;
  onOpen?: () => void;
  onRename?: () => void;
  onShare?: () => void;
  onDuplicate: () => void;
  publish?: { label: string; blocked: string | null; onClick: () => void };
  onDiscardDraft?: () => void;
  onUnpublish?: () => void;
  onDelete: () => void;
}) {
  return (
    <>
      {onFill ? (
        <>
          <MenuItem
            onClick={onFill}
            {...(fillBlocked ? { disabled: true, disabledReason: fillBlocked } : {})}
          >
            <Icon color="muted" name="clipboard-check" size="md" />
            <Text size="sm">Fill out on a Project…</Text>
          </MenuItem>
          <MenuSeparator />
        </>
      ) : null}
      {onOpen ? (
        <MenuItem onClick={onOpen}>
          <Icon color="muted" name="pencil" size="md" />
          <Text size="sm">Open in builder</Text>
        </MenuItem>
      ) : null}
      {onRename ? (
        <MenuItem onClick={onRename}>
          <Icon color="muted" name="text-cursor-input" size="md" />
          <Text size="sm">Rename</Text>
        </MenuItem>
      ) : null}
      <MenuItem onClick={onDuplicate}>
        <Icon color="muted" name="copy" size="md" />
        <Text size="sm">Duplicate</Text>
      </MenuItem>
      {publish ? (
        <MenuItem
          onClick={publish.onClick}
          {...(publish.blocked ? { disabled: true, disabledReason: publish.blocked } : {})}
        >
          <Icon color="muted" name="eye" size="md" />
          <Text size="sm">{publish.label}</Text>
        </MenuItem>
      ) : null}
      {onDiscardDraft ? (
        <MenuItem onClick={onDiscardDraft}>
          <Icon color="muted" name="undo" size="md" />
          <Text size="sm">Discard unpublished changes</Text>
        </MenuItem>
      ) : null}
      {onUnpublish ? (
        <MenuItem onClick={onUnpublish}>
          <Icon color="muted" name="eye-closed" size="md" />
          <Text size="sm">Unpublish</Text>
        </MenuItem>
      ) : null}
      {onShare ? (
        <>
          <MenuSeparator />
          <MenuItem onClick={onShare}>
            <Icon color="muted" name="share" size="md" />
            <Text size="sm">Sharing & Permissions</Text>
          </MenuItem>
        </>
      ) : null}
      <MenuSeparator />
      <MenuItem onClick={onDelete} variant="destructive">
        <Icon name="trash" size="md" />
        <Text size="sm">Delete</Text>
      </MenuItem>
    </>
  );
}
